import { useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { MvmoTermsState, MvnoCheckValue, VulnerableCheckState } from 'src/recoil/common';
import { TelecomTypeState, TimerState, TransIdState } from 'src/recoil/join';
import {
  AllCheckState,
  CertNumberSubmitState,
  JoinStepState,
  RecoilUiList,
  TermsAgreeState,
} from 'src/recoil/ui';
import { formatPhoneNumber, forwardPlaystore, showMaintenanceAlert } from 'src/utils/Common';
import { isCertNumberValidator, isPhoneNumberValidator } from 'src/utils/Validator';
import useAPI from './useApi';
import { useInput } from './useInput';
import { useModal, useModalWithPromise } from './useModal';
import { useTrackingPage } from 'src/hooks/useTrackingPage';
import { join_complete_greenp } from 'src/utils/greenp';
import { captureMvnoTermsError } from 'src/utils/sentry/validation';
import { monitoredHrefReplace } from 'src/utils/sentry/navigation';

export const useSubmit = () => {
  const [transId, setTransId] = useRecoilState(TransIdState);
  const setJoinStep = useSetRecoilState(JoinStepState);
  const setCertNumberSubmit = useSetRecoilState(CertNumberSubmitState);
  const setTimer = useSetRecoilState(TimerState);
  const setMvnoTerms = useSetRecoilState(MvmoTermsState);
  const setMvnoCheckValue = useSetRecoilState(MvnoCheckValue);
  const setVulnerableCheck = useSetRecoilState(VulnerableCheckState);
  const [termsAgree, setTermsAgree] = useRecoilState(TermsAgreeState);

  const telecomType = useRecoilValue(TelecomTypeState);
  const mvnoCheckValue = useRecoilValue(MvnoCheckValue);
  const { termsData } = useRecoilValue(RecoilUiList);
  const [isAllCheck, setAllCheck] = useRecoilState(AllCheckState);
  const resetTermsAgree = useResetRecoilState(TermsAgreeState);
  const { isGreenpTracking } = useTrackingPage();

  const modal = useModal();
  const vulnerableModal = useModalWithPromise();

  const phoneInput = useInput('', isPhoneNumberValidator);
  const certificationInput = useInput('', isCertNumberValidator);
  const { mutateAsync: CheckMvmoSubmit } = useAPI('CheckMvnoMember').useSetMutation({
    Telecom: telecomType,
    PNumber: phoneInput.value,
  });
  const { mutateAsync: CheckVulnerableSubmit, isLoading: vulnerableLoading } = useAPI(
    'CheckVulnerableMember',
  ).useSetMutation({
    Telecom: telecomType,
    PNumber: phoneInput.value,
  });

  // ref는 동기 연타를, state는 선행 API/모달 대기 구간의 버튼 비활성을 담당한다.
  const isJoinSubmittingRef = useRef(false);
  const [isJoinSubmitting, setIsJoinSubmitting] = useState(false);
  const isCertSubmittingRef = useRef(false);
  const [isCertSubmitting, setIsCertSubmitting] = useState(false);

  const lockCertSubmitting = () => {
    if (isCertSubmittingRef.current) return false;
    isCertSubmittingRef.current = true;
    setIsCertSubmitting(true);
    return true;
  };
  const unlockCertSubmitting = () => {
    isCertSubmittingRef.current = false;
    setIsCertSubmitting(false);
  };

  const termsStatus = () => {
    if (!isAllCheck) {
      if (
        window.confirm(
          '서비스 이용을 위하여 약관동의가 필요합니다.\n모두(선택약관 미동의) 동의하시겠습니까?',
        )
      ) {
        const ArrCopy = [...termsAgree];
        termsData.Terms.forEach((terms) => {
          if (terms.Order !== undefined) {
            ArrCopy[terms.Order - 1] = terms.Required ?? 0;
          }
        });
        setTermsAgree(ArrCopy);
        setAllCheck(true);
        return true;
      } else {
        return false;
      }
    } else return true;
  };

  const validationCert = async (query: any) => {
    //MVNO KT 약관 체크 로직
    showMaintenanceAlert();

    if (phoneInput.value.length !== 11) {
      alert('휴대폰번호를 정확하게 입력하여 주십시오.');
      return false;
    }
    if (!termsStatus()) return false;
    if (!lockCertSubmitting()) return false;

    try {
      setMvnoCheckValue(false);
      if (telecomType === 1) {
        const data: any = await CheckMvmoSubmit();
        captureMvnoTermsError(data?.Body);
        if (data?.Body?.Type === 1) {
          setMvnoTerms({ Terms: data.Body.Terms, TermsTitle: data.Body.TermsTitle });
          modal.toggle();
          return false;
        }
      }
      setCertNumberSubmit(true);
      await sendCert(query);
    } finally {
      unlockCertSubmitting();
    }
  };
  const cert = async (query: any) => {
    // MVNO 약관 모달에서 재진입하는 경로
    if (!lockCertSubmitting()) return false;

    try {
      setCertNumberSubmit(true);
      return await sendCert(query);
    } finally {
      unlockCertSubmitting();
    }
  };
  const sendCert = (query: any) => {
    //전화번호 인증
    return query()
      .then(async (data: any) => {
        if (data?.Header?.ErrCode === '10010001') {
          setTimer(data.Body.TimeOut);
          setTransId(data.Body.TranID);
          setJoinStep(1);
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'complete_sms' });
          return true;
        }
        if (data?.Header?.ErrCode === '00100305') {
          // 알뜰폰 사용자 리다이렉트 처리
          if (!!data.Body.URL) monitoredHrefReplace(data.Body.URL, 'mvno-redirect');
        }
        if (data?.Header?.ErrCode === '10020022') {
          // 차단 사용자 페이지 새로고침 처리
          window.location.reload();
          return false;
        }
      })
      .catch((error: any) => {
        console.error(error);
        return false;
      });
  };
  const join = async (query: any) => {
    switch (true) {
      case phoneInput.value === '':
        alert('휴대폰번호를 입력하여 주세요.');
        return false;
      case transId === '':
        alert('인증번호 받기를 먼저 시도하여 주세요.');
        return false;
      case certificationInput.value.length !== 4:
        alert('인증번호를 입력하여 주세요.');
        return false;
      default:
    }
    if (isJoinSubmittingRef.current) return false;
    isJoinSubmittingRef.current = true;
    setIsJoinSubmitting(true);

    try {
      if (!termsStatus()) return false;
      await setTermsAgreeMvno(1);
      const joinConfirmed = await checkVulnerableMember();
      if (!joinConfirmed) return false;
      await query()
        .then((data: any) => {
          if (data?.Header?.ErrCode === '00000000') {
            if (isGreenpTracking) {
              join_complete_greenp(transId);
            }
            window.phone_number = formatPhoneNumber(phoneInput.value);
            window.dataLayer.push({ event: 'complete_signup' });
            handleReset();
            alert(
              '휴대폰분실보호 안내\n휴대폰 분실, 도난 피해 발생 시 신속한 회수를 도와주며 데이터를 안전하게 보호할 수 있는 안심 유료부가서비스에 가입되셨습니다. 원활한 이용을 위해 [확인] 버튼을 눌러 앱스토어로 이동하세요.',
            );
            forwardPlaystore();
          } else if (data?.Header?.ErrCode === '10020002') {
            // 인증번호 오류
            certificationInput.onResetHandler();
            setTermsAgreeMvno(0);
          } else if (data?.Header?.ErrCode === '10020004') {
            // 인증번호 횟수 초과
            certificationInput.onResetHandler();
            setJoinStep(0);
            setTermsAgreeMvno(0);
          } else {
            setTermsAgreeMvno(0);
            return false;
          }
        })
        .catch((error: any) => {
          return error;
        });
    } finally {
      isJoinSubmittingRef.current = false;
      setIsJoinSubmitting(false);
    }
  };
  const setTermsAgreeMvno = async (flag: number) => {
    if (telecomType === 1 && mvnoCheckValue) {
      await new Promise((resolve) => {
        setTermsAgree((prev) => {
          const termsAgreeCopy = [...prev];
          termsAgreeCopy[8] = flag;
          resolve(termsAgreeCopy);
          return termsAgreeCopy;
        });
      });
    }
  };
  const checkVulnerableMember = async () => {
    const data = await CheckVulnerableSubmit();
    const { Vulnerable, AgePopUpYN } = data.Body;

    if (Vulnerable === 'Y' || AgePopUpYN === 'Y') {
      setVulnerableCheck(true);
      const userConfirmed = await vulnerableModal.open();
      return userConfirmed;
    }

    return true;
  };

  const handleReset = () => {
    phoneInput.onResetHandler();
    certificationInput.onResetHandler();
    setJoinStep(0);
    resetTermsAgree();
    setCertNumberSubmit(false);
  };

  return {
    modal,
    vulnerableModal,
    join,
    cert,
    validationCert,
    phoneInput,
    certificationInput,
    handleReset,
    vulnerableLoading,
    isJoinSubmitting,
    isCertSubmitting,
  };
};
